/**
 * The four theme skins, ported verbatim from the interactive design spec
 * (`Telegram Lists.dc.html`, `SKINS`). Do not "improve" these values — they are
 * the reference Telegram client palettes the spec was reviewed against.
 *
 * Inside Telegram every var that has a `themeParams` key is overwritten at
 * runtime from `WebApp.themeParams` (see `applyTelegramThemeParams`). These
 * tables are the browser fallback, selected by platform + `prefers-color-scheme`.
 *
 * `--tg-separator` and `--tg-chip-bg` have no Telegram param, so they stay
 * derived per-skin constants and are never overwritten.
 */

export const SKIN_NAMES = ['ios-light', 'ios-dark', 'mat-light', 'mat-dark'] as const;

export type SkinName = (typeof SKIN_NAMES)[number];

export type ThemeVars = Record<string, string>;

export interface Skin {
  label: string;
  /** Swatch colour used by the `?devtheme=1` switcher. */
  swatch: string;
  /** `ios` cards use radius 10, `material` cards radius 12. */
  family: 'ios' | 'material';
  scheme: 'light' | 'dark';
  vars: ThemeVars;
}

export const SKINS: Record<SkinName, Skin> = {
  'ios-light': {
    label: 'iOS Light',
    swatch: '#007aff',
    family: 'ios',
    scheme: 'light',
    vars: {
      '--tg-bg': '#efeff4',
      '--tg-secondary-bg': '#efeff4',
      '--tg-section-bg': '#ffffff',
      '--tg-text': '#000000',
      '--tg-hint': '#8e8e93',
      '--tg-subtitle': '#8e8e93',
      '--tg-link': '#007aff',
      '--tg-accent-text': '#007aff',
      '--tg-button': '#007aff',
      '--tg-button-text': '#ffffff',
      '--tg-destructive': '#ff3b30',
      '--tg-header-bg': '#f7f7f8',
      '--tg-section-header-text': '#6d6d72',
      '--tg-separator': '#d5d5da',
      '--tg-skeleton': 'linear-gradient(90deg,#e6e6ec 25%,#f2f2f6 37%,#e6e6ec 63%)',
      '--tg-toast-bg': '#2c2c2e',
      '--tg-toast-text': '#ffffff',
      '--tg-chip-bg': '#eaf3ff',
    },
  },
  'ios-dark': {
    label: 'iOS Dark',
    swatch: '#3e88f7',
    family: 'ios',
    scheme: 'dark',
    vars: {
      '--tg-bg': '#000000',
      '--tg-secondary-bg': '#000000',
      '--tg-section-bg': '#1c1c1e',
      '--tg-text': '#ffffff',
      '--tg-hint': '#8d8d93',
      '--tg-subtitle': '#98989e',
      '--tg-link': '#3e88f7',
      '--tg-accent-text': '#3e88f7',
      '--tg-button': '#3e88f7',
      '--tg-button-text': '#ffffff',
      '--tg-destructive': '#ff453a',
      '--tg-header-bg': '#1c1c1e',
      '--tg-section-header-text': '#8d8e93',
      '--tg-separator': '#38383a',
      '--tg-skeleton': 'linear-gradient(90deg,#232325 25%,#2e2e30 37%,#232325 63%)',
      '--tg-toast-bg': '#2c2c2e',
      '--tg-toast-text': '#ffffff',
      '--tg-chip-bg': '#1b2a3f',
    },
  },
  'mat-light': {
    label: 'Material Light',
    swatch: '#40a7e3',
    family: 'material',
    scheme: 'light',
    vars: {
      '--tg-bg': '#ffffff',
      '--tg-secondary-bg': '#f0f0f0',
      '--tg-section-bg': '#ffffff',
      '--tg-text': '#222222',
      '--tg-hint': '#8a8a8a',
      '--tg-subtitle': '#8a8a8a',
      '--tg-link': '#2481cc',
      '--tg-accent-text': '#3a95d5',
      '--tg-button': '#40a7e3',
      '--tg-button-text': '#ffffff',
      '--tg-destructive': '#cc2929',
      '--tg-header-bg': '#ffffff',
      '--tg-section-header-text': '#3a95d5',
      '--tg-separator': '#e4e4e4',
      '--tg-skeleton': 'linear-gradient(90deg,#ececec 25%,#f6f6f6 37%,#ececec 63%)',
      '--tg-toast-bg': '#323232',
      '--tg-toast-text': '#ffffff',
      '--tg-chip-bg': '#e8f3fb',
    },
  },
  'mat-dark': {
    label: 'Material Dark',
    swatch: '#50a8eb',
    family: 'material',
    scheme: 'dark',
    vars: {
      '--tg-bg': '#212d3b',
      '--tg-secondary-bg': '#151e27',
      '--tg-section-bg': '#17212b',
      '--tg-text': '#ffffff',
      '--tg-hint': '#6d7f8f',
      '--tg-subtitle': '#6d7f8f',
      '--tg-link': '#248bda',
      '--tg-accent-text': '#64b5ef',
      '--tg-button': '#50a8eb',
      '--tg-button-text': '#ffffff',
      '--tg-destructive': '#ec3942',
      '--tg-header-bg': '#17212b',
      '--tg-section-header-text': '#64b5ef',
      '--tg-separator': '#101921',
      '--tg-skeleton': 'linear-gradient(90deg,#1c2530 25%,#243040 37%,#1c2530 63%)',
      '--tg-toast-bg': '#111c26',
      '--tg-toast-text': '#ffffff',
      '--tg-chip-bg': '#1c3a52',
    },
  },
};

/**
 * `WebApp.themeParams` key -> CSS custom property. Only these are overwritten
 * inside Telegram; anything absent from this map (separator, chip, skeleton,
 * toast) stays on the per-skin fallback.
 */
export const TELEGRAM_PARAM_TO_VAR: Record<string, string> = {
  bg_color: '--tg-bg',
  secondary_bg_color: '--tg-secondary-bg',
  section_bg_color: '--tg-section-bg',
  text_color: '--tg-text',
  hint_color: '--tg-hint',
  subtitle_text_color: '--tg-subtitle',
  link_color: '--tg-link',
  accent_text_color: '--tg-accent-text',
  button_color: '--tg-button',
  button_text_color: '--tg-button-text',
  destructive_text_color: '--tg-destructive',
  header_bg_color: '--tg-header-bg',
  section_header_text_color: '--tg-section-header-text',
};
